import * as React from "react"
import { usePage } from "@inertiajs/react"
import { ChevronDown, ChevronRight } from "lucide-react"
import { useLocalePath } from "@/lib/locale-path"
import { cn } from "@/lib/utils"
import type { ConditionMenuGroup, PageProps } from "@/types/inertia"

interface Props {
  label: string
  href: string
  menu: ConditionMenuGroup[]
  // What the chevron announces to screen readers. Defaults to the conditions
  // menu's wording, which is what the first usage of this component needs.
  browseLabel?: string
  // The trigger label and chevron colours. Gisela's nav sits on a solid cream
  // bar, so the defaults are ink; the venue's nav is transparent over its
  // heroes until scrolled and passes white until then (PublicNavDropdown).
  triggerClassName?: string
  chevronClassName?: string
}

// Hand-rolled rather than Radix on purpose: Radix's dropdown is click-driven
// and portals its content out of the server-rendered HTML, so with SSR on in
// production a crawler would see a nav with no condition links in it at all.
// Here every panel stays in the DOM and is only hidden with visibility
// classes — the links are always in the markup, always crawlable.
const PANEL =
  "min-w-64 divide-y divide-navy-light rounded-xl border border-navy-light bg-navy-mid py-2 shadow-lg transition-opacity duration-150"
// The submenu scrolls rather than running off the bottom of a laptop screen.
// The outer panel deliberately does not: an overflow container there would
// clip the very flyout it is meant to show.
const SUBPANEL = `${PANEL} max-h-[calc(100vh-8rem)] overflow-y-auto`
const ITEM = "block px-4 py-2 text-sm text-white no-underline transition-colors hover:bg-navy"
// Long enough to cross the gap between the trigger and a submenu without the
// menu snapping shut under the pointer.
const CLOSE_DELAY = 150

// A nav item with a menu under it — "What I help with" and "Therapies I
// offer" both use this. The label still navigates to the index page, while
// hover (or the chevron, for touch and keyboard) opens the menu. The first
// group's items sit inline; each later group becomes a row with its own side
// flyout, so a single-group menu (the therapies) is a plain inline list.
export default function GiselaNavDropdown({ label, href, menu, browseLabel, triggerClassName, chevronClassName }: Props) {
  const { url, props } = usePage<PageProps>()
  const t = props.translations.gisela_nav
  const lp = useLocalePath()

  const [open, setOpen] = React.useState(false)
  const [openGroup, setOpenGroup] = React.useState<string | null>(null)
  const closeTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const groupTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const triggerRef = React.useRef<HTMLAnchorElement>(null)

  // Both states are mirrored into refs, because the toggles below have to read
  // what is open *right now* rather than what the last render saw: mouseenter,
  // pointerdown and click all land within one frame, so a handler closing over
  // the state value can easily be a render behind.
  const openRef = React.useRef(false)
  const groupRef = React.useRef<string | null>(null)

  function showPanel(value: boolean) {
    openRef.current = value
    setOpen(value)
  }

  function showGroup(value: string | null) {
    groupRef.current = value
    setOpenGroup(value)
  }

  // What was open when the pointer went down. Tapping fires the compatibility
  // mouseenter *after* pointerdown, so without this a touch user's first tap
  // would open the panel and then immediately toggle it shut again.
  const openBeforePress = React.useRef<boolean | null>(null)
  const groupBeforePress = React.useRef<string | null | undefined>(undefined)

  function clearTimers() {
    if (closeTimer.current) clearTimeout(closeTimer.current)
    if (groupTimer.current) clearTimeout(groupTimer.current)
    closeTimer.current = null
    groupTimer.current = null
  }

  function closeAll() {
    clearTimers()
    showPanel(false)
    showGroup(null)
  }

  // Same pattern as the burger panel: a navigation closes the menu.
  React.useEffect(() => {
    closeAll()
  }, [url])

  React.useEffect(() => () => clearTimers(), [])

  const inlineItems = menu.filter((group) => !group.flyout).flatMap((group) => group.items)
  const flyoutGroups = menu.filter((group) => group.flyout)

  const triggerClass = cn(
    "whitespace-nowrap text-sm no-underline transition-colors hover:opacity-70",
    triggerClassName ?? "text-ink-body",
  )
  const chevronClass = cn(
    "inline-flex h-6 w-6 items-center justify-center rounded-full transition-colors",
    chevronClassName ?? "text-ink-muted hover:bg-surface hover:text-ink-body",
  )

  if (menu.length === 0) {
    return (
      <a href={lp(href)} className={triggerClass}>
        {label}
      </a>
    )
  }

  return (
    <div
      className="relative"
      onMouseEnter={() => {
        clearTimers()
        showPanel(true)
      }}
      onMouseLeave={() => {
        clearTimers()
        closeTimer.current = setTimeout(closeAll, CLOSE_DELAY)
      }}
      // Tabbing into the menu opens it; tabbing out of it closes it again.
      // Landing on the label itself is the exception: the chevron beside it is
      // the next tab stop and opens the menu, and leaving the label inert is
      // what stops Escape — which closes and returns focus here — from
      // instantly reopening what it just closed.
      onFocus={(e) => {
        if (!openRef.current && triggerRef.current?.contains(e.target as Node)) return
        clearTimers()
        showPanel(true)
      }}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) closeAll()
      }}
      onKeyDown={(e) => {
        if (e.key !== "Escape") return
        closeAll()
        triggerRef.current?.focus()
      }}
    >
      <div className="flex items-center gap-1">
        {/* The label always navigates — the chevron is the menu control, so a
            touch user can browse without being taken off the page first. */}
        <a ref={triggerRef} href={lp(href)} className={triggerClass}>
          {label}
        </a>
        <button
          type="button"
          onPointerDown={() => {
            openBeforePress.current = openRef.current
          }}
          onClick={() => {
            const wasOpen = openBeforePress.current ?? openRef.current
            openBeforePress.current = null
            showPanel(!wasOpen)
          }}
          aria-haspopup="true"
          aria-expanded={open}
          aria-label={browseLabel ?? t.browse_help_with}
          className={chevronClass}
        >
          <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-180")} />
        </button>
      </div>

      {/* pt-3 is a deliberate transparent bridge: without it the pointer
          crosses a gap between trigger and panel and the menu closes. */}
      <div
        className={cn(
          "absolute left-0 top-full z-50 pt-3",
          open ? "visible" : "pointer-events-none invisible",
        )}
      >
        <div className={cn(PANEL, open ? "opacity-100" : "opacity-0")}>
          {inlineItems.map((item) => (
            <a key={item.path} href={lp(item.path)} className={ITEM}>
              {item.label}
            </a>
          ))}

          {flyoutGroups.map((group) => {
            const groupOpen = openGroup === group.label

            return (
              <div
                key={group.label}
                className="relative"
                onMouseEnter={() => {
                  if (groupTimer.current) clearTimeout(groupTimer.current)
                  showGroup(group.label)
                }}
                onMouseLeave={() => {
                  if (groupTimer.current) clearTimeout(groupTimer.current)
                  groupTimer.current = setTimeout(() => showGroup(null), CLOSE_DELAY)
                }}
              >
                <button
                  type="button"
                  onPointerDown={() => {
                    groupBeforePress.current = groupRef.current
                  }}
                  onClick={() => {
                    // Same tap-order problem as the chevron above.
                    const before = groupBeforePress.current === undefined
                      ? groupRef.current
                      : groupBeforePress.current
                    groupBeforePress.current = undefined
                    showGroup(before === group.label ? null : group.label)
                  }}
                  onFocus={() => showGroup(group.label)}
                  aria-haspopup="true"
                  aria-expanded={groupOpen}
                  className={cn(
                    "flex w-full items-center justify-between gap-4 px-4 py-2 text-left text-sm text-white transition-colors hover:bg-navy",
                    groupOpen && "bg-navy",
                  )}
                >
                  {group.label}
                  <ChevronRight className="h-3.5 w-3.5 shrink-0 text-white/70" />
                </button>

                {/* Bottom-aligned with its row: the flyout rows sit at the
                    foot of the panel, so growing upwards keeps a long second
                    tier on screen instead of running off the bottom. */}
                <div
                  className={cn(
                    "absolute bottom-0 left-full z-50 pl-1",
                    groupOpen ? "visible" : "pointer-events-none invisible",
                  )}
                >
                  <div className={cn(SUBPANEL, groupOpen ? "opacity-100" : "opacity-0")}>
                    {group.items.map((item) => (
                      <a key={item.path} href={lp(item.path)} className={ITEM}>
                        {item.label}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
